import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  LinearProgress,
  Link,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import History from "@mui/icons-material/History";
import MailOutline from "@mui/icons-material/MailOutline";
import NotesOutlined from "@mui/icons-material/NotesOutlined";
import PaymentsOutlined from "@mui/icons-material/PaymentsOutlined";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import SchoolOutlined from "@mui/icons-material/SchoolOutlined";

import agent from "../../utils/axios";
import { hasAnyRole } from "../../utils/auth";
import { bucketFor } from "../../utils/billBucket";
import { formatDate, formatMoney, formatPaymentDate } from "../../utils/format";
import { monoFontFamily } from "../../theme/tokens";
import {
  billChipMeta,
  billStatusMeta,
  Callout,
  PageHeader,
  paymentStatusMeta,
  StatusChip,
  useToast,
} from "../../components/ui";
import CompanySelectDialog, { QuickBooksCompany } from "./CompanySelectDialog";
import { useConvertToCheck } from "./useConvertToCheck";
import {
  AddressParts,
  addressLines,
  billTimeline,
  convertedMessage,
  errorMessageOf,
  fullName,
  isAchMethod,
  NO_REASON_RECORDED,
  paidToName,
  PersonName,
  TimelineEvent,
} from "./billHelpers";

/** The bill fields this screen shows (picked from the API's Bill entity). */
type BillDetail = {
  requestingApplication: string | null;
  requestingClient: string | null;
  programName: string | null;
  amount: number | null;
  paymentMethod: string | null;
  status: string | null;
  invoiceId: string | null;
  payTo: string | null;
  name: string | null;
  notes: string | null;
  createdDate: string | null;
  lastQBRetryDate: string | null;
  qbLastErrorCode: string | null;
  address: AddressParts | null;
  paymentService: {
    paymentDate: string | null;
    paymentStatus: string | null;
    paymentRefCode: string | null;
    errorMessage: string | null;
  } | null;
};

/**
 * The payee fields this screen shows. Only these are kept from the response, so no tax
 * identifier (which GetByRefcode also returns) is ever held in state or rendered.
 */
type PayeeDetail = {
  id: string;
  name: PersonName | null;
  email: string | null;
  bill: BillDetail;
};

const str = (value: unknown): string | null =>
  typeof value === "string" ? value : null;

function toDetail(response: any): PayeeDetail | null {
  const raw = Array.isArray(response?.bills) ? response.bills[0] : undefined;
  if (!response || !raw) return null;
  const amount = Number(raw.amount);
  const address = raw.address;
  const payment = raw.paymentService;
  return {
    id: str(response.id) ?? "",
    name: response.name
      ? {
          first: str(response.name.first),
          middle: str(response.name.middle),
          last: str(response.name.last),
        }
      : null,
    email: str(response.email),
    bill: {
      requestingApplication: str(raw.requestingApplication),
      requestingClient: str(raw.requestingClient),
      programName: str(raw.programName),
      amount:
        raw.amount === null ||
        raw.amount === undefined ||
        !Number.isFinite(amount)
          ? null
          : amount,
      paymentMethod: str(raw.paymentMethod),
      status: str(raw.status),
      invoiceId: str(raw.invoiceId),
      payTo: str(raw.payTo),
      name: str(raw.name),
      notes: str(raw.notes),
      createdDate: str(raw.createdDate),
      lastQBRetryDate: str(raw.lastQBRetryDate),
      qbLastErrorCode: str(raw.qbLastErrorCode),
      address: address
        ? {
            address1: str(address.address1),
            address2: str(address.address2),
            address3: str(address.address3),
            city: str(address.city),
            state: str(address.state),
            postalCode: str(address.postalCode),
          }
        : null,
      paymentService: payment
        ? {
            paymentDate: str(payment.paymentDate),
            paymentStatus: str(payment.paymentStatus),
            paymentRefCode: str(payment.paymentRefCode),
            errorMessage: str(payment.errorMessage),
          }
        : null,
    },
  };
}

/** Card with an icon + title header, as on the AB#6872 Bill detail preview. */
function SectionCard({
  icon,
  title,
  children,
  flush = false,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  /** Content runs edge to edge (tables). */
  flush?: boolean;
}) {
  return (
    <Card>
      <CardHeader
        avatar={icon}
        title={title}
        titleTypographyProps={{ variant: "h6", component: "h2" }}
        sx={{
          px: 4,
          py: 3,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiCardHeader-avatar": {
            mr: 2,
            display: "flex",
            color: "text.secondary",
          },
        }}
      />
      {flush ? (
        children
      ) : (
        <CardContent sx={{ p: 4, "&:last-child": { pb: 4 } }}>
          {children}
        </CardContent>
      )}
    </Card>
  );
}

/** One label/value pair in the Payment card's description list. */
function Field({
  label,
  children,
  wide = false,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <Box sx={{ minWidth: 0, gridColumn: wide ? { sm: "span 2" } : undefined }}>
      <Box
        component="dt"
        sx={{
          mb: 1,
          fontSize: "0.6875rem",
          fontWeight: 500,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        {label}
      </Box>
      <Box
        component="dd"
        sx={{
          m: 0,
          fontSize: "0.875rem",
          color: "text.primary",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

const Muted = ({ children }: { children: React.ReactNode }) => (
  <Box component="span" sx={{ color: "text.secondary" }}>
    {children}
  </Box>
);

function Timeline({ events }: { events: TimelineEvent[] }) {
  const last = events.length - 1;
  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, p: 0 }}>
      {events.map((event, index) => {
        const meta = [event.date ? formatDate(event.date) : null, event.detail]
          .filter(Boolean)
          .join(" · ");
        return (
          <Box
            component="li"
            key={event.key}
            sx={{
              position: "relative",
              display: "flex",
              gap: 3,
              pb: index < last ? 4 : 0,
            }}
          >
            {index < last && (
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  left: "8px",
                  top: "20px",
                  bottom: 0,
                  width: "2px",
                  backgroundColor: "divider",
                }}
              />
            )}
            <RadioButtonUnchecked
              aria-hidden
              sx={(theme) => ({
                flex: "none",
                fontSize: 18,
                color: theme.palette.status[event.tone].fg,
              })}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {event.title}
              </Typography>
              {meta && (
                <Typography
                  variant="caption"
                  component="p"
                  color="text.secondary"
                >
                  {meta}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function DetailSkeleton() {
  return (
    <Box
      aria-hidden
      sx={{
        display: "grid",
        gap: 4,
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 7fr) minmax(0, 4fr)" },
      }}
    >
      {[0, 1].map((column) => (
        <Card key={column}>
          <CardContent sx={{ p: 4, display: "grid", gap: 2 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" />
            <Skeleton variant="text" />
            <Skeleton variant="text" width="70%" />
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

function BillDetails() {
  const { billRefCode } = useParams();
  const toast = useToast();
  const [payee, setPayee] = useState<PayeeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  // AB#5377: the API refuses a BillRefCode carried by more than one bill (409) rather than
  // returning an arbitrary one. Without somewhere to put that, the component rendered an empty
  // receipt — blank amount, blank payee, and today's date in place of the payment date.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const canConvertToCheck = hasAnyRole(["Admin", "Developer"]);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const { convert, busy: converting } = useConvertToCheck();

  const requestRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Fetches the bill. 'initial' shows the loading state and, on failure, the warning;
   * 'refresh' (after Convert to check) updates the bill in place and reports failure.
   */
  const loadBill = useCallback(
    async (mode: "initial" | "refresh"): Promise<boolean> => {
      const requestId = ++requestRef.current;
      const isCurrent = () =>
        mountedRef.current && requestId === requestRef.current;
      if (mode === "initial") {
        setLoading(true);
        setLoadError(null);
      }
      try {
        const response = await agent.Bills.getBillByRefCode(billRefCode ?? "");
        if (!isCurrent()) return true;
        const detail = toDetail(response);
        if (detail) {
          setPayee(detail);
          setLoadError(null);
        } else if (mode === "initial") {
          setPayee(null);
          setLoadError("This bill could not be loaded.");
        }
        return true;
      } catch (error) {
        if (!isCurrent()) return true;
        if (mode === "initial") {
          setPayee(null);
          setLoadError(errorMessageOf(error, "This bill could not be loaded."));
        }
        return false;
      } finally {
        if (mode === "initial" && isCurrent()) setLoading(false);
      }
    },
    [billRefCode]
  );

  useEffect(() => {
    loadBill("initial");
  }, [loadBill, reloadKey]);

  // Convert to check for this one bill: company picker, then CSI Pay's stored token or the
  // QuickBooks sign-in popup; the bill then refreshes in place (now Check / Paid).
  const handleCompanyConfirm = async (company: QuickBooksCompany) => {
    try {
      const outcome = await convert([billRefCode ?? ""], company.realmId);
      if (!mountedRef.current) return;
      setCompanyDialogOpen(false);
      if (outcome === "signInIncomplete") {
        toast.error(
          "QuickBooks sign-in didn't finish, so the bill was not converted."
        );
        return;
      }
      toast.success(convertedMessage(1));
      const refreshed = await loadBill("refresh");
      if (!refreshed && mountedRef.current) {
        toast.error(
          "The bill was converted, but it couldn't be reloaded. Refresh the page to see its new status."
        );
      }
    } catch (error) {
      if (!mountedRef.current) return;
      setCompanyDialogOpen(false);
      toast.error(errorMessageOf(error, "Convert to check didn't finish."));
      loadBill("refresh");
    }
  };

  const ref = billRefCode ?? "";
  const breadcrumbs = [{ label: "Bills", to: "/bills" }, { label: ref }];

  if (loading) {
    return (
      <React.Fragment>
        <PageHeader
          title={`Bill ${ref}`}
          helmetTitle={`Bill ${ref}`}
          breadcrumbs={breadcrumbs}
        />
        <LinearProgress aria-label="Loading bill" sx={{ mb: 4 }} />
        <DetailSkeleton />
      </React.Fragment>
    );
  }

  // AB#5377: show why the bill could not be loaded instead of an empty receipt.
  if (loadError || !payee) {
    return (
      <React.Fragment>
        <PageHeader
          title={`Bill ${ref}`}
          helmetTitle={`Bill ${ref}`}
          breadcrumbs={breadcrumbs}
        />
        <Callout
          tone="warn"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setReloadKey((key) => key + 1)}
            >
              Retry
            </Button>
          }
        >
          {loadError ?? "This bill could not be loaded."}
        </Callout>
      </React.Fragment>
    );
  }

  const bill = payee.bill;
  const payeeName = fullName(payee.name);
  const paidTo = paidToName(bill, payeeName);
  const paymentStatus = (bill.paymentService?.paymentStatus ?? "").trim();
  const paymentRef = (bill.paymentService?.paymentRefCode ?? "").trim();
  // The shared bill-bucket rule (Dashboard, Tipalti): only a paid bill shows its date.
  const paid =
    bucketFor(bill.status, bill.paymentService?.paymentStatus) === "paid";
  const failureReason = (bill.paymentService?.errorMessage ?? "").trim();
  const address = addressLines(bill.address);
  const email = (payee.email ?? "").trim();
  const notes = (bill.notes ?? "").trim();
  const invoiceId = (bill.invoiceId ?? "").trim();
  const client = (bill.requestingClient ?? "").trim();
  // Same rule as before AB#6872 (Admin/Developer and an ACH bill), made null-safe.
  const showConvert = canConvertToCheck && isAchMethod(bill.paymentMethod);

  const subtitleParts: React.ReactNode[] = [];
  if (payeeName) {
    subtitleParts.push(
      payee.id ? (
        <Link
          key="payee"
          component={RouterLink}
          to={`/payees/detail/${payee.id}`}
        >
          {payeeName}
        </Link>
      ) : (
        payeeName
      )
    );
  }
  if (invoiceId) subtitleParts.push(`Invoice ${invoiceId}`);
  if (client) subtitleParts.push(client);
  const subtitle =
    subtitleParts.length > 0
      ? subtitleParts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && " · "}
            {part}
          </React.Fragment>
        ))
      : undefined;

  return (
    <React.Fragment>
      <PageHeader
        title={`Bill ${ref}`}
        helmetTitle={`Bill ${ref}`}
        subtitle={subtitle}
        breadcrumbs={breadcrumbs}
        actions={
          <>
            <StatusChip
              meta={billChipMeta(
                bill.status,
                bill.paymentService?.paymentStatus
              )}
            />
            {showConvert && (
              <Button
                variant="outlined"
                startIcon={<MailOutline />}
                onClick={() => setCompanyDialogOpen(true)}
                disabled={converting}
              >
                Convert to check…
              </Button>
            )}
          </>
        }
      />

      {bucketFor(bill.status, bill.paymentService?.paymentStatus) ===
        "failed" && (
        <Callout
          tone="err"
          role="alert"
          title="This payment failed"
          sx={{ mb: 4 }}
        >
          {failureReason || NO_REASON_RECORDED} Update the payee, or convert the
          bill to a check.
        </Callout>
      )}

      <Box
        sx={{
          display: "grid",
          gap: 4,
          alignItems: "start",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 7fr) minmax(0, 4fr)",
          },
        }}
      >
        <Box sx={{ display: "grid", gap: 4, minWidth: 0 }}>
          <SectionCard icon={<PaymentsOutlined />} title="Payment">
            <Box
              component="dl"
              sx={{
                m: 0,
                display: "grid",
                columnGap: 4,
                rowGap: 4,
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                },
              }}
            >
              <Field label="Payment ref">
                {paymentRef ? (
                  <Box
                    component="span"
                    sx={{ fontFamily: monoFontFamily, fontSize: "0.8125rem" }}
                  >
                    {paymentRef}
                  </Box>
                ) : (
                  <Muted>Assigned when paid</Muted>
                )}
              </Field>
              <Field label="Payment date">
                {/* A stored date on an unpaid bill is stale: only a paid bill shows it (AC6). */}
                {formatPaymentDate(
                  paid ? bill.paymentService?.paymentDate : null
                )}
              </Field>
              <Field label="Paid to">{paidTo || <Muted>—</Muted>}</Field>
              <Field label="Client">{client || <Muted>—</Muted>}</Field>
              <Field label="Requesting application">
                {(bill.requestingApplication ?? "").trim() || <Muted>—</Muted>}
              </Field>
              <Field label="Method">
                {(bill.paymentMethod ?? "").trim() || <Muted>—</Muted>}
              </Field>
              <Field label="Payment status">
                <StatusChip
                  size="small"
                  meta={
                    paymentStatus
                      ? paymentStatusMeta(paymentStatus)
                      : billStatusMeta(bill.status)
                  }
                />
              </Field>
              <Field label="Address" wide>
                {address.length > 0 ? (
                  address.map((line, index) => (
                    <Box component="span" key={index} sx={{ display: "block" }}>
                      {line}
                    </Box>
                  ))
                ) : (
                  <Muted>—</Muted>
                )}
              </Field>
              <Field label="Email">
                {email ? (
                  <Link href={`mailto:${email}`}>{email}</Link>
                ) : (
                  <Muted>—</Muted>
                )}
              </Field>
            </Box>
          </SectionCard>

          <SectionCard icon={<SchoolOutlined />} title="Programs" flush>
            <Table aria-label="Programs">
              <TableHead>
                <TableRow>
                  <TableCell>Program</TableCell>
                  <TableCell align="right">Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell>
                    {(bill.programName ?? "").trim() || <Muted>—</Muted>}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    {formatMoney(bill.amount)}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      borderTop: 2,
                      borderTopColor: "divider",
                      borderBottom: 0,
                    }}
                  >
                    Total
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 500,
                      fontVariantNumeric: "tabular-nums",
                      borderTop: 2,
                      borderTopColor: "divider",
                      borderBottom: 0,
                    }}
                  >
                    {formatMoney(bill.amount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </SectionCard>
        </Box>

        <Box sx={{ display: "grid", gap: 4, minWidth: 0 }}>
          <SectionCard icon={<History />} title="What happened">
            <Timeline events={billTimeline(bill)} />
          </SectionCard>

          {notes && (
            <SectionCard icon={<NotesOutlined />} title="Notes">
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {notes}
              </Typography>
            </SectionCard>
          )}
        </Box>
      </Box>

      <CompanySelectDialog
        open={companyDialogOpen}
        bills={[{ name: paidTo || payeeName, amount: bill.amount }]}
        busy={converting}
        onClose={() => setCompanyDialogOpen(false)}
        onConfirm={handleCompanyConfirm}
      />
    </React.Fragment>
  );
}

export default BillDetails;
